<template>
  <div class="ai-composer" :class="{ 'ai-composer--disabled': disabled }">
    <div class="ai-composer__column">
      <div class="ai-composer__box">
        <textarea
          ref="input"
          class="ai-composer__input"
          :value="value"
          :placeholder="placeholder"
          :disabled="disabled"
          rows="1"
          @input="onInput"
          @keydown.enter.exact.prevent="onSend"
        ></textarea>

        <div class="ai-composer__foot">
          <!-- The picker always names the active Skill, including the General
               Assistant. The Skill governs the answer and travels with each
               message, so "which one am I in" should never require inference —
               sending into the wrong Skill costs a generation and a quota hit,
               and the only other feedback is the change marker appearing after
               the fact. -->
          <b-dropdown
            dropup
            no-caret
            variant="link"
            toggle-class="ai-skill__toggle"
            menu-class="ai-skill__menu"
            :disabled="disabled"
          >
            <template #button-content>
              <i class="fa fa-angle-down"></i>
              <span class="ai-skill__name">{{ activeSkillName }}</span>
            </template>

            <b-dropdown-item
              v-for="skill in skills"
              :key="skill.id"
              :active="skill.id === activeSkillId"
              @click="$emit('update:activeSkillId', skill.id)"
            >
              <div class="ai-skill__label">{{ skill.name }}</div>
              <div v-if="skill.description" class="ai-skill__description">
                {{ skill.description }}
              </div>
            </b-dropdown-item>
          </b-dropdown>

          <div class="ai-composer__spacer"></div>

          <span v-if="counterVisible" class="ai-composer__counter" :class="{ 'is-over': overLimit }">
            {{ value.length }} / {{ maxChars }}
          </span>

          <button
            type="button"
            class="ai-composer__send"
            :disabled="!canSend"
            :title="disabled ? 'Waiting for the previous answer' : 'Send'"
            @click="onSend"
          >
            <i class="fa fa-arrow-right"></i>
          </button>
        </div>
      </div>

      <p v-if="disabled" class="ai-composer__hint">
        Waiting for the previous answer before you can send again.
      </p>
    </div>
  </div>
</template>

<script>
const MAX_INPUT_HEIGHT_PX = 200;

export default {
  name: "MessageComposer",

  props: {
    value: {
      type: String,
      default: "",
    },
    skills: {
      type: Array,
      default: () => [],
    },
    activeSkillId: {
      type: String,
      default: null,
    },
    /**
     * Locked while a message is unresolved.
     *
     * Not cosmetic: a second message sent during generation takes a higher `seq`, so
     * the pending answer would insert above it on arrival and reorder the transcript
     * under the user.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * `max_message_chars` from the sender's quota profile. Null until ENG-2310
     * exposes it — the backend refuses over-length messages either way, this only
     * saves the user a wasted round trip.
     */
    maxChars: {
      type: Number,
      default: null,
    },
  },

  computed: {
    activeSkill() {
      return this.skills.find((skill) => skill.id === this.activeSkillId) || null;
    },

    activeSkillName() {
      return this.activeSkill ? this.activeSkill.name : "";
    },

    overLimit() {
      return this.maxChars !== null && this.value.length > this.maxChars;
    },

    counterVisible() {
      // Only worth showing as the limit comes into range; a permanent counter on a
      // chat input is noise.
      return this.maxChars !== null && this.value.length > this.maxChars * 0.8;
    },

    canSend() {
      return !this.disabled && this.value.trim().length > 0 && !this.overLimit;
    },

    placeholder() {
      return this.disabled ? "" : "Ask something, or paste something to pull apart…";
    },
  },

  watch: {
    value() {
      this.$nextTick(this.resize);
    },
  },

  mounted() {
    this.focus();
    this.resize();
  },

  methods: {
    onInput(event) {
      this.$emit("input", event.target.value);
    },

    onSend() {
      if (!this.canSend) {
        return;
      }
      this.$emit("send");
    },

    focus() {
      if (this.$refs.input && !this.disabled) {
        this.$refs.input.focus();
      }
    },

    resize() {
      const el = this.$refs.input;
      if (!el) {
        return;
      }
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT_PX)}px`;
      el.style.overflowY = el.scrollHeight > MAX_INPUT_HEIGHT_PX ? "auto" : "hidden";
    },
  },
};
</script>

<style scoped>
.ai-composer {
  flex: 0 0 auto;
  padding: 8px 12px 12px;
}

.ai-composer__column {
  max-width: 760px;
  margin: 0 auto;
}

/* Static white with the same PayAngel green wrap as the chat surface above it.
   The shimmer belongs to the page behind, not to anything holding text. */
.ai-composer__box {
  border: 2px solid #6eb644;
  border-radius: 12px;
  background: #fff;
}

/* Muted while locked, so "you cannot type yet" is visible at a glance. */
.ai-composer--disabled .ai-composer__box {
  background: #f7f8fa;
  border-color: #a8cf8c;
}

.ai-composer__input {
  display: block;
  width: 100%;
  padding: 12px 14px 4px;
  border: 0;
  background: none;
  resize: none;
  line-height: 1.55;
  outline: none;
}

.ai-composer__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 8px 10px;
}

.ai-composer__spacer {
  flex: 1 1 auto;
}

.ai-composer__counter {
  font-size: 12px;
  color: #8a91a0;
}

.ai-composer__counter.is-over {
  color: #c0392b;
  font-weight: 500;
}

.ai-composer__send {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: #2f3542;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.ai-composer__send:disabled {
  opacity: 0.35;
  cursor: default;
}

.ai-composer__hint {
  margin: 8px 2px 0;
  font-size: 12px;
  color: #8a91a0;
}

/* Skill picker ----------------------------------------------------------- */

.ai-composer >>> .ai-skill__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 7px;
  color: #6b7285;
  text-decoration: none;
  font-size: 13px;
}

.ai-composer >>> .ai-skill__toggle:hover {
  background: #f0f2f5;
  color: #2f3542;
}

.ai-skill__name {
  font-weight: 500;
}

.ai-composer >>> .ai-skill__menu {
  min-width: 280px;
  max-width: 340px;
  padding: 6px;
  border: 1px solid #e3e6ec;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(24, 30, 44, 0.12);
}

.ai-composer >>> .ai-skill__menu .dropdown-item {
  padding: 8px 10px;
  border-radius: 7px;
  white-space: normal;
}

.ai-skill__label {
  font-size: 13px;
  font-weight: 500;
}

.ai-skill__description {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: #8a91a0;
}

.ai-composer >>> .dropdown-item.active .ai-skill__description {
  color: rgba(255, 255, 255, 0.75);
}

/* Dark theme ------------------------------------------------------------- */

body.full-dark .ai-composer__box {
  background: #232938;
}

body.full-dark .ai-composer--disabled .ai-composer__box {
  opacity: 0.75;
}

body.full-dark .ai-composer__input {
  color: #e4e7ee;
}

body.full-dark .ai-composer__send {
  background: #4d7cfe;
}

body.full-dark .ai-composer >>> .ai-skill__menu {
  border-color: #333c50;
  background: #232938;
}
</style>
